import { Component, HostListener, effect, inject } from '@angular/core';
import { Router, RouterOutlet } from '@angular/router';
import { ConfirmationModalComponent } from './shared/components/confirmation-modal/confirmation-modal.component';
import { NavigationLoaderComponent } from './shared/components/navigation-loader/navigation-loader.component';
import { UpdateAvailableBannerComponent } from './shared/components/update-available-banner/update-available-banner.component';
import { UpdateAvailableModalComponent } from './shared/components/update-available-modal/update-available-modal.component';
import { AppUpdateService } from './core/app-update.service';
import { MajorEventTypes } from './core/models/major-event.types';
import { logRealtimeLogin } from './core/utils/workflow-realtime-debug.util';
import { AuthService } from './core/services/auth.service';
import { FacilityContextService } from './core/services/facility-context.service';
import { NotificationService } from './core/services/notification.service';
import { AuthRedirectService } from './core/services/auth-redirect.service';
import { SessionLifecycleService } from './core/services/session-lifecycle.service';
import { TimezoneService } from './core/services/timezone.service';
import { WorkflowEventsService } from './core/services/workflow/workflow-events.service';
import { WorkflowInvalidationService } from './core/services/workflow/workflow-invalidation.service';
import { isPatientAssessmentRoute } from './core/utils/synexa-perform-url.util';

const USE_LOCAL_API_KEY = 'useLocalApi';

@Component({
  selector: 'app-root',
  imports: [
    RouterOutlet,
    NavigationLoaderComponent,
    ConfirmationModalComponent,
    UpdateAvailableModalComponent,
    UpdateAvailableBannerComponent
  ],
  templateUrl: './app.component.html',
  styleUrl: './app.component.scss'
})
export class AppComponent {
  private readonly auth = inject(AuthService);
  private readonly facilityContext = inject(FacilityContextService);
  private readonly notifications = inject(NotificationService);
  private readonly workflowEvents = inject(WorkflowEventsService);
  private readonly workflowInvalidation = inject(WorkflowInvalidationService);
  private readonly router = inject(Router);
  /** Ensures force-logout for suspended/inactive accounts is wired for the app lifetime. */
  private readonly sessionLifecycle = inject(SessionLifecycleService);
  /** Redirects to login when the session ends on a protected route. */
  private readonly authRedirect = inject(AuthRedirectService);
  private readonly appUpdate = inject(AppUpdateService);
  /** Instantiated at the root so the display timezone is published before any page formats a date. */
  private readonly timezone = inject(TimezoneService);

  constructor() {
    this.workflowEvents.events$.subscribe((event) => {
      this.workflowInvalidation.emitFromMajorEvent(event);
      this.maybeToastAutoWorkflowEvent(event.eventType, event.description);
    });

    effect(() => {
      if (this.auth.isAuthenticated()) {
        logRealtimeLogin('Authenticated — starting SignalR hub');
        void this.workflowEvents.start();
      } else {
        this.workflowEvents.stop();
      }
    });

    effect(() => {
      if (!this.auth.isAuthenticated()) return;
      const facilityId = this.facilityContext.selectedFacilityId();
      // The selected site's own organization, not the home one: hub groups are keyed by the
      // facility's org, and FacilityContextService.selectFacility joins with it too — using the home
      // org here would race it and leave a global user on the wrong group at a cross-org site.
      const tenantId = this.facilityContext.selectedOrganizationId();
      if (!tenantId) return;

      // Tenant group first, and without requiring a facility — events that carry a tenant but no
      // facility (session-link delivery receipts) target it, and "All facilities" selects none.
      void this.workflowEvents.joinTenant(tenantId);

      if (facilityId) {
        void this.workflowEvents.joinFacility(tenantId, facilityId);
      }
    });
  }

  private maybeToastAutoWorkflowEvent(eventType: string | undefined, description?: string): void {
    if (isPatientAssessmentRoute(this.router.url ?? '')) {
      return;
    }

    const message = description?.trim();
    if (!message) return;

    // EpisodeStatusChanged is not toasted here — local actions already show success
    // (e.g. "Episode cancelled.") and the SignalR echo would duplicate it.
    switch (eventType) {
      case MajorEventTypes.NcTestCompleted:
      case MajorEventTypes.NcBatteryCompleted:
      case MajorEventTypes.ClinicalDocumentationStatusChanged:
        this.notifications.push({ type: 'info', message });
        break;
      default:
        break;
    }
  }

  @HostListener('document:keydown', ['$event'])
  onKeyDown(event: KeyboardEvent): void {
    if (window.location.href.includes('login')) return;

    if (event.ctrlKey && event.key === '2') {
      event.preventDefault();
      event.stopPropagation();
      const token = this.auth.token();
      if (token) {
        void navigator.clipboard.writeText(token);
        this.notifications.push({ type: 'success', message: 'Token copied to clipboard' });
      }
    }

    if (event.ctrlKey && event.altKey && event.key === '3') {
      event.preventDefault();
      event.stopPropagation();
      const useLocal = localStorage.getItem(USE_LOCAL_API_KEY) === 'true';
      if (useLocal) {
        localStorage.removeItem(USE_LOCAL_API_KEY);
        this.notifications.push({ type: 'info', message: 'Live environment enabled' });
      } else {
        localStorage.setItem(USE_LOCAL_API_KEY, 'true');
        this.notifications.push({ type: 'info', message: 'Local environment enabled' });
      }
      window.setTimeout(() => window.location.reload(), 700);
    }

    if (event.ctrlKey && event.shiftKey && event.key.toLowerCase() === 'u') {
      event.preventDefault();
      event.stopPropagation();
      this.appUpdate.previewUpdateModal();
    }
  }
}
